/*
 * Hero slider video loader
 * ------------------------
 * Loaded by sections/hero-slider.liquid ONLY when at least one slide in the hero
 * slider actually has a video set. A homepage with image-only slides never requests
 * this file, so adding the capability costs nothing until a video is used.
 *
 * What it does, and why each part matters for page speed:
 *
 *  - The <video> elements are rendered with no src and preload="none", so the page
 *    load itself never fetches a video. This script attaches a <source> and calls
 *    play() only once a slide is genuinely being shown.
 *  - It picks ONE file per visitor (desktop or mobile rendition) via matchMedia,
 *    rather than rendering both and letting the browser download two.
 *  - In fade mode every slide occupies the same box, so "is it in the viewport" is
 *    not enough on its own. A slide only loads when Swiper has marked it active
 *    (or when Swiper has not initialised yet and it is the first slide).
 *  - Playback pauses when the tab is hidden and when the slide stops being active,
 *    which keeps CPU and battery use off the table on long homepage sessions.
 *  - Sound is never possible: the element is muted in markup and muted again here
 *    (muted, defaultMuted and volume), and no controls are exposed.
 *  - prefers-reduced-motion: reduce means the poster image simply stays, no video.
 */
(function () {
  'use strict';

  var DESKTOP = window.matchMedia('(min-width: 1024px)');
  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  function sourceFor(video) {
    var desktop = video.getAttribute('data-src-desktop') || '';
    var mobile = video.getAttribute('data-src-mobile') || '';
    var preferred = DESKTOP.matches ? desktop : mobile;
    /* No cross-fallback: a device with no video of its own keeps showing the poster. */
    return preferred;
  }

  function silence(video) {
    video.muted = true;
    video.defaultMuted = true;
    try {
      video.volume = 0;
    } catch (e) {
      /* some browsers disallow setting volume programmatically; muted already covers us */
    }
  }

  function attemptPlay(video) {
    if (REDUCED_MOTION.matches) return;
    silence(video);
    var played = video.play();
    if (played && typeof played.catch === 'function') {
      played.catch(function () {
        /* autoplay refused (rare when muted) - the poster image stays visible */
      });
    }
  }

  function load(video) {
    if (video.getAttribute('data-hero-video-loaded') === 'true') return;
    var url = sourceFor(video);
    if (!url) return;

    video.setAttribute('data-hero-video-loaded', 'true');
    silence(video);
    video.setAttribute('preload', 'metadata');

    video.addEventListener(
      'playing',
      function () {
        video.classList.add('is-playing');
      },
      { once: true }
    );

    var source = document.createElement('source');
    source.setAttribute('src', url);
    source.setAttribute('type', 'video/mp4');
    video.appendChild(source);
    video.load();

    attemptPlay(video);
  }

  /*
   * A slide is eligible when Swiper says it is the active one. Before Swiper
   * initialises there are no active classes at all, so fall back to "first slide",
   * which is the one on screen at that point.
   */
  function isEligible(video) {
    var slide = video.closest ? video.closest('.swiper-slide') : null;
    if (!slide) return true;

    var wrapper = slide.parentElement;
    if (!wrapper) return true;

    if (wrapper.querySelector('.swiper-slide-active, .swiper-slide-duplicate-active')) {
      return (
        slide.classList.contains('swiper-slide-active') ||
        slide.classList.contains('swiper-slide-duplicate-active')
      );
    }

    return slide === wrapper.firstElementChild;
  }

  /*
   * "Play video on mobile only" (set per slide in the theme editor). The slide wrapper
   * carries data-video-mobile-only; on desktop widths the video is never loaded or
   * played, so the desktop poster image is all a desktop visitor gets and no video
   * bytes are downloaded.
   */
  function blockedOnDesktop(video) {
    return DESKTOP.matches && !!(video.closest && video.closest('[data-video-mobile-only]'));
  }

  function init() {
    var videos = document.querySelectorAll('video[data-hero-video]');
    if (!videos.length) return;

    var list = Array.prototype.slice.call(videos);

    function evaluate(video, inViewport) {
      if (blockedOnDesktop(video)) {
        if (video.getAttribute('data-hero-video-loaded') === 'true') video.pause();
        return;
      }
      if (!isEligible(video)) {
        if (video.getAttribute('data-hero-video-loaded') === 'true') video.pause();
        return;
      }
      if (inViewport === false) {
        if (video.getAttribute('data-hero-video-loaded') === 'true') video.pause();
        return;
      }
      if (video.getAttribute('data-hero-video-loaded') === 'true') {
        attemptPlay(video);
      } else {
        load(video);
      }
    }

    var inViewport = {};

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var index = list.indexOf(entry.target);
            inViewport[index] = entry.isIntersecting;
            evaluate(entry.target, entry.isIntersecting);
          });
        },
        { rootMargin: '200px 0px' }
      );
      list.forEach(function (video) {
        observer.observe(video);
      });
    } else {
      list.forEach(function (video) {
        evaluate(video, true);
      });
    }

    /* Swiper swaps the active class as slides change; re-evaluate when it does. */
    if ('MutationObserver' in window) {
      var wrappers = [];
      list.forEach(function (video) {
        var slide = video.closest ? video.closest('.swiper-slide') : null;
        var wrapper = slide && slide.parentElement;
        if (wrapper && wrappers.indexOf(wrapper) === -1) wrappers.push(wrapper);
      });

      var classWatcher = new MutationObserver(function () {
        list.forEach(function (video, index) {
          var visible = inViewport[index];
          evaluate(video, visible === undefined ? true : visible);
        });
      });

      wrappers.forEach(function (wrapper) {
        classWatcher.observe(wrapper, {
          attributes: true,
          attributeFilter: ['class'],
          subtree: true
        });
      });
    }

    /* Crossing the desktop breakpoint (resize / rotate) re-checks the mobile-only rule. */
    var onBreakpoint = function () {
      list.forEach(function (video, index) {
        var visible = inViewport[index];
        evaluate(video, visible === undefined ? true : visible);
      });
    };
    if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', onBreakpoint);
    else if (DESKTOP.addListener) DESKTOP.addListener(onBreakpoint);

    document.addEventListener('visibilitychange', function () {
      list.forEach(function (video, index) {
        if (document.hidden) {
          video.pause();
          return;
        }
        var visible = inViewport[index];
        evaluate(video, visible === undefined ? true : visible);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
